import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import Breadcrumbs from '../../components/Breadcrumbs';
import StoreCTA from '../../components/StoreCTA';
import '../../styles/gameInfo.css';

function NonogramsPage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': 'What other names do Nonograms go by?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Nonograms are also called picture logic puzzles or paint-by-number logic puzzles. These names all refer to the same type of picture logic puzzle where you fill cells based on row and column clues.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Do I need to guess when solving Nonograms?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'No. Every well-designed Nonogram has a unique solution that can be reached through logical deduction alone. Our puzzles are all verified to be solvable without guessing.'
        }
      },
      {
        '@type': 'Question',
        'name': 'How do I read Nonogram clues?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'The numbers along each row and column tell you the lengths of consecutive filled groups in that line. For example, "3 1" means there is a group of 3 filled cells, at least one empty cell, then a group of 1 filled cell.'
        }
      },
      {
        '@type': 'Question',
        'name': 'What is the overlap technique in Nonograms?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'The overlap technique compares the leftmost and rightmost possible positions for a block. Cells that are filled in both positions must be filled, giving you guaranteed progress without guessing.'
        }
      }
    ]
  };

  const videoGameSchema = {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      "name": "Nonograms",
      "description": "Picture logic puzzles (paint-by-number logic) - reveal hidden images from row and column number clues.",
      "url": "https://logicpuzzles.ca/games/nonograms",
      "image": "https://logicpuzzles.ca/nonograms/icons/Icon-512.png",
      "genre": "Logic Puzzle",
      "gamePlatform": "Web Browser",
      "operatingSystem": "Any (browser-based)",
      "playMode": "SinglePlayer",
      "inLanguage": "en",
      "isAccessibleForFree": true,
      "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "CAD",
          "availability": "https://schema.org/InStock"
      },
      "publisher": {
          "@id": "https://logicpuzzles.ca/#organization"
      },
      "potentialAction": {
          "@type": "PlayAction",
          "target": "https://logicpuzzles.ca/play/nonograms"
      }
  };

  return (
    <div>
      <Helmet>
        <title>Nonogram Puzzles - Free Picture Logic Puzzles Online | Logic Puzzles CA</title>
        <meta name="description" content="Solve free Nonogram puzzles online! Also called paint-by-number logic, these picture logic puzzles reveal hidden images through pure deduction. Print and play offline." />
        <meta name="keywords" content="nonogram puzzle, picture logic puzzle, paint by numbers logic, pixel picture puzzle, nonogram online, printable nonogram" />
        <meta property="og:title" content="Nonogram Puzzles - Free Picture Logic Puzzles Online" />
        <meta property="og:description" content="Reveal hidden pictures through logic! Solve free Nonogram puzzles with row and column clues. No guessing required." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://logicpuzzles.ca/nonograms/icons/Icon-512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Nonogram Puzzles - Free Picture Logic Puzzles Online" />
        <meta name="twitter:description" content="Reveal hidden pictures through logic! Solve free Nonogram puzzles with row and column clues." />
        <link rel="canonical" href="https://logicpuzzles.ca/games/nonograms" />
        <script type="application/ld+json">{JSON.stringify(videoGameSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <NavBar />
      <Breadcrumbs categoryName="Logic Games" categoryPath="/logic-games" currentLabel="Nonograms" />

      <header className="gi-hero">
        <div className="container gi-hero__inner">
          <div>
            <p className="eyebrow">Logic Puzzle · Free online</p>
            <h1>Nonogram Puzzles</h1>
            <p className="gi-hero__lead">Reveal hidden pictures through pure logical deduction</p>
            <div className="gi-hero__actions">
              <Link to="/play/nonograms" className="button">▶ Play now</Link>
              <a href="#how-to-play" className="button secondary">How to solve</a>
              <Link to="/printable/nonograms" className="button secondary">Printable puzzles</Link>
            </div>
          </div>
          <div className="gi-hero__art" aria-hidden="true">
            <img src="/nonograms/icons/Icon-192.png" alt="" width="168" height="168" />
          </div>
        </div>
      </header>

      <div className="gi-main">
        <StoreCTA app="dlp" placement="game-page" page="/games/nonograms" headline="Enjoying Nonograms? Get eight new logic puzzles every day in our free app" />

        <section className="gi-section">
          <p className="gi-lead">
            <strong>Nonogram puzzles</strong>, also known as <strong>paint-by-number logic puzzles</strong>,
            are <strong>picture logic puzzles</strong> where you fill in cells on a grid to reveal a hidden image.
            Using only the number clues along each row and column, you deduce which cells to fill and which to
            leave empty. Every puzzle has exactly one solution reachable through <strong>pure logical
            deduction</strong> - no guessing required.
          </p>
        </section>

        <section className="gi-section" id="how-to-play">
          <h2>How to Solve Nonogram Puzzles</h2>
          <div className="gi-grid">
            <div className="gi-card">
              <span className="gi-card__num">01</span>
              <h3>Reading Clues</h3>
              <p>Numbers along each row and column indicate consecutive groups of filled cells, separated by at least one empty cell.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">02</span>
              <h3>Overlap Technique</h3>
              <p>Compare the leftmost and rightmost possible positions for each block. Cells that overlap in both positions must be filled.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">03</span>
              <h3>Mark Empty Cells</h3>
              <p>Use X marks to indicate cells that must be empty. This helps narrow down possibilities for remaining blocks.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">04</span>
              <h3>Edge Logic</h3>
              <p>When a filled cell touches the edge of the grid, you know exactly where that block starts or ends.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">05</span>
              <h3>Full Lines</h3>
              <p>If the clues add up to the full line length (including gaps), the entire line is determined immediately.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">06</span>
              <h3>Cross-Reference</h3>
              <p>Progress in one line reveals information for crossing lines. Alternate between rows and columns for steady progress.</p>
            </div>
          </div>
        </section>

        <section className="gi-section">
          <h2>Advanced Nonogram Solving Techniques</h2>
          <p>
            Mastering <strong>Nonogram puzzles</strong> beyond the basics requires combining multiple
            deduction strategies. These techniques help you tackle larger and more challenging grids.
          </p>
          <ul className="gi-list">
            <li>Use the overlap method on every line to find guaranteed fills</li>
            <li>Look for completed lines and mark all remaining cells as empty</li>
            <li>Track which blocks are partially placed to narrow their positions</li>
            <li>Work on lines with the most information first (largest clues, most cells known)</li>
            <li>Use contradiction: assume a cell is filled/empty and check if it leads to an impossibility</li>
            <li>On larger puzzles, focus on the edges and corners first for quick progress</li>
          </ul>
        </section>

        <section className="gi-section">
          <h2>Nonogram Puzzle FAQ</h2>
          <div className="gi-faq">
            <details open>
              <summary>What other names do Nonograms go by?</summary>
              <p>You may see them called picture logic puzzles or paint-by-number logic puzzles. They all follow the same rules.</p>
            </details>
            <details>
              <summary>What does the clue "0" mean?</summary>
              <p>A clue of "0" means the entire row or column is empty - no cells should be filled in that line.</p>
            </details>
            <details>
              <summary>Where should I start solving?</summary>
              <p>Start with lines that have the largest clues relative to line length. These give the most overlap and guaranteed fills.</p>
            </details>
          </div>
        </section>

        <section className="gi-cta">
          <h2>Ready to Reveal Hidden Pictures?</h2>
          <p>
            Challenge yourself with <strong>Nonogram puzzles</strong> from 5x5 to 20x20. Each puzzle
            reveals a unique pattern through pure <strong>logical deduction</strong>. Print them out and
            solve anywhere - no screen required.
          </p>
          <div className="gi-cta__actions">
            <Link to="/printable/nonograms" className="button">Print Nonograms</Link>
            <Link to="/logic-games" className="button secondary">More Logic Puzzles</Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default NonogramsPage;
